import {TagPlugin} from '../tagPlugin';
import {Event} from '../../event';
import {Queue} from './queue';
import {sleep} from '../../utils/time';
import {debug, warn} from '../../utils/log';
import {isLocalStorageAvailable} from '../../utils/localStorage';
import {govolteEventFromEvent, GovolteEventQueryParams} from './govolteEvent';
import {
  eventToJsonEvent,
  jsonEventToShortenedFormat,
  JsonEvent,
} from './jsonEvent';
import qs from 'qs';
import {EventType} from '../../eventType';
import {newStreamWaypointEventThrottler} from '../../streamWaypointEventThrottler';
import {throttleEvent} from '../../eventThrottler';

const TAG = 'GovoltePlugin';

const GOVOLTE_DEFAULT_BASE_URL = 'https://topspin.npo.nl';
const GOVOLTE_ENDPOINTS = {
  submitEvent: 'web-event',
};

export const STORAGE_NAME = 'Govolte_queue';
export const STORAGE_LIMIT = 1_000; // events

export const REQUEST_TIMEOUT_SECONDS = 5_000;

export const DEFAULT_DELAY_BETWEEN_RETRY_IN_MS = 5_000;
export const DEFAULT_MAX_RETRY_COUNT = 3;
export const DEFAULT_DELAY_BETWEEN_STREAM_WAYPOINTS_IN_MS = 30_000;

const MAX_DELAY_BETWEEN_RETRY_IN_MS = 30_000;
const MIN_DELAY_BETWEEN_RETRY_IN_MS = 0;

const MAX_MAXIMUM_RETRY_COUNT = 10;
const MIN_MAXIMUM_RETRY_COUNT = 0;

const MAX_DELAY_BETWEEN_STREAM_WAYPOINTS_IN_MS = 30_000;
const MIN_DELAY_BETWEEN_STREAM_WAYPOINTS_IN_MS = 0;

export const DEFAULT_BATCH_SIZE = 20;
export const DEFAULT_MAX_BATCH_AGE = 30_000;

const isNonRetryableJsonStatus = (status: number) =>
  status >= 400 && status < 500 && status !== 408 && status !== 429;

/**
 * Govolte plugin
 * - Includes a queue system where it tries to resend any failed event to
 *   Govolte URL as many as {@link maxRetryCount}
 */
export interface GovoltePlugin extends TagPlugin {
  /**
   * Return of all events that are not yet sent
   */
  queuedEvents(): GovolteEventQueryParams[];

  /**
   * True if plugin is busy with sending an event (actually sending object through API
   * or waiting to retry sending failed API event)
   */
  isSendingEvent(): boolean;

  /**
   * Max number of retries before ditching an event.
   */
  maxRetryCount: number;

  /**
   * Number of ms between each retry call.
   */
  delayBetweenRetriesInMs: number;

  /**
   * Minimum number of ms between a stream-related event and a `streamWaypoint` event
   */
  delayBetweenStreamWaypointsInMs: number;

  /**
   * Whether to use batching mode (true) or send individual events (false)
   */
  is_batch: boolean;

  /**
   * Number of events to batch before sending (only used when is_batch is true)
   */
  batch_size: number;

  /**
   * Maximum time in milliseconds to wait before sending a batch (only used when is_batch is true)
   */
  max_batch_age: number;
}

/**
 * Options object for {@link GovoltePlugin}
 * @property maxRetryCount max number of retries before ditching an event.
 *   For example, if you set it to 3 then it will do 4 trials.
 *   Value should be between 0 and 10.
 * @property delayBetweenRetriesInMs number of ms between each retry call.
 *   It will be multiplied by the retry count so that each retry waits for a little longer.
 *   Value should be between 0 and 30_000.
 * @property delayBetweenStreamWaypointsInMs number of ms between `streamWaypoint` events.
 *   Other stream-related events will also reset this interval.
 *   Value should be between 0 and 30_000
 * @property baseUrl destination url to which events will be sent.
 * @property is_batch whether to use batching mode (true) or send individual events (false).
 * @property batch_size number of events to batch before sending (only used when is_batch is true).
 * @property max_batch_age maximum time in milliseconds to wait before sending a batch (only used when is_batch is true).
 */
export type GovoltePluginOptions = {
  maxRetryCount: number;
  delayBetweenRetriesInMs: number;
  delayBetweenStreamWaypointsInMs: number;
  baseUrl: string;
  is_batch: boolean;
  batch_size: number;
  max_batch_age: number;
};

const DEFAULT_GOVOLTE_OPTIONS: GovoltePluginOptions = {
  maxRetryCount: DEFAULT_MAX_RETRY_COUNT,
  delayBetweenRetriesInMs: DEFAULT_DELAY_BETWEEN_RETRY_IN_MS,
  delayBetweenStreamWaypointsInMs: DEFAULT_DELAY_BETWEEN_STREAM_WAYPOINTS_IN_MS,
  baseUrl: GOVOLTE_DEFAULT_BASE_URL,
  is_batch: false,
  batch_size: DEFAULT_BATCH_SIZE,
  max_batch_age: DEFAULT_MAX_BATCH_AGE,
};

/**
 * Event that includes how many times the trial has been done
 */
interface QueueEvent extends GovolteEventQueryParams {
  trialCount: number;
  // Store original event data for JSON conversion when not batching
  originalEventType?: EventType;
  originalEvent?: Event;
}

type SendEventResult = 'sent' | 'dropped' | 'retry';

/**
 * JSON event with retry information for batching mode
 */
interface BatchedQueueEvent {
  eventType: EventType;
  event: Event;
  jsonEvent: JsonEvent;
  trialCount: number;
  timestamp: number;
}

/**
 * Instantiate new Govolte plugin
 * @param options Optional configuration options (see: {@link GovoltePluginOptions})
 * @returns A configured {@link GovoltePlugin} object
 */
export function newGovoltePlugin(
  options?: Partial<GovoltePluginOptions>
): GovoltePlugin {
  const pluginOptions: GovoltePluginOptions = {
    ...DEFAULT_GOVOLTE_OPTIONS,
    ...options,
  };

  const eventThrottlers = [
    newStreamWaypointEventThrottler(
      pluginOptions.delayBetweenStreamWaypointsInMs
    ),
  ];

  // Make sure that maxRetryCount, delayBetweenRetriesInMs and delayBetweenStreamTimeEventsInMs
  // are between the min and max allowed values
  const maxRetryCount = pluginOptions.maxRetryCount!;
  if (maxRetryCount < MIN_MAXIMUM_RETRY_COUNT) {
    throw Error(
      `Invalid maxRetryCount(${maxRetryCount}). Minimum maxRetryCount is ${MIN_MAXIMUM_RETRY_COUNT}.`
    );
  }

  if (maxRetryCount > MAX_MAXIMUM_RETRY_COUNT) {
    throw Error(
      `Invalid maxRetryCount(${maxRetryCount}). Maximum maxRetryCount is ${MAX_MAXIMUM_RETRY_COUNT}.`
    );
  }

  const delayBetweenRetriesInMs = pluginOptions.delayBetweenRetriesInMs!;
  if (delayBetweenRetriesInMs < MIN_DELAY_BETWEEN_RETRY_IN_MS) {
    throw Error(
      `Invalid delayBetweenRetriesInMs(${delayBetweenRetriesInMs}). Minimum delayBetweenRetriesInMs is ${MIN_DELAY_BETWEEN_RETRY_IN_MS}.`
    );
  }

  if (delayBetweenRetriesInMs > MAX_DELAY_BETWEEN_RETRY_IN_MS) {
    throw Error(
      `Invalid delayBetweenRetriesInMs(${delayBetweenRetriesInMs}). Maximum delayBetweenRetriesInMs is ${MAX_DELAY_BETWEEN_RETRY_IN_MS}.`
    );
  }

  const delayBetweenStreamWaypointsInMs =
    pluginOptions.delayBetweenStreamWaypointsInMs!;
  if (
    delayBetweenStreamWaypointsInMs < MIN_DELAY_BETWEEN_STREAM_WAYPOINTS_IN_MS
  ) {
    throw Error(
      `Invalid delayBetweenStreamTimeEventsInMs(${delayBetweenStreamWaypointsInMs}). Minimum delayBetweenStreamTimeEventsInMs is ${MIN_DELAY_BETWEEN_STREAM_WAYPOINTS_IN_MS}.`
    );
  }

  if (
    delayBetweenStreamWaypointsInMs > MAX_DELAY_BETWEEN_STREAM_WAYPOINTS_IN_MS
  ) {
    throw Error(
      `Invalid delayBetweenStreamTimeEventsInMs(${delayBetweenStreamWaypointsInMs}). Maximum delayBetweenStreamTimeEventsInMs is ${MAX_DELAY_BETWEEN_STREAM_WAYPOINTS_IN_MS}.`
    );
  }

  const queue = new Queue<QueueEvent>();
  let isSendingEvent = false;

  // Batching variables
  const batchQueue = new Queue<BatchedQueueEvent>();
  let batchTimer: number | null = null;
  let isSendingBatch = false;

  /**
   * Load events from local storage if it's available
   */
  function loadPreviousEventsFromLocalStorage() {
    if (!isLocalStorageAvailable()) {
      warn(
        TAG,
        "Local storage is unavailable. Event queue won't be saved to local storage."
      );
      return;
    }
    const prevItems = localStorage.getItem(STORAGE_NAME);
    if (!prevItems) {
      return;
    }

    const parsedItems = JSON.parse(prevItems) as QueueEvent[];
    // Filter out queued stream stop events (see TOPSLA-219)
    const filteredItems = parsedItems.filter(
      item => item.t !== EventType.STREAM_STOP
    );

    if (pluginOptions.is_batch) {
      // Convert old individual events to batch format and add to batch queue
      debug(
        TAG,
        `Converting ${filteredItems.length} old individual events to batch format`
      );

      filteredItems.forEach(queueEvent => {
        // Only convert if we have the original event data
        if (queueEvent.originalEventType && queueEvent.originalEvent) {
          try {
            const jsonEvent = eventToJsonEvent(
              queueEvent.originalEventType,
              queueEvent.originalEvent
            );
            const batchedEvent: BatchedQueueEvent = {
              eventType: queueEvent.originalEventType,
              event: queueEvent.originalEvent,
              jsonEvent,
              trialCount: queueEvent.trialCount || 0,
              timestamp: Date.now(),
            };
            batchQueue.enqueue(batchedEvent);
            debug(
              TAG,
              `Converted event ${queueEvent.originalEventType} to batch format`
            );
          } catch (err) {
            warn(
              TAG,
              `Failed to convert event ${queueEvent.originalEventType} to batch format`,
              err
            );
            // Fall back to individual queue for this event
            queue.enqueue(queueEvent);
          }
        } else {
          // No original event data, keep in individual queue
          debug(
            TAG,
            `Event ${queueEvent.t} missing original data, keeping in individual queue`
          );
          queue.enqueue(queueEvent);
        }
      });

      // Clear localStorage after successful conversion
      localStorage.removeItem(STORAGE_NAME);

      // Start batch timer if we have events
      if (!batchQueue.isEmpty()) {
        debug(TAG, 'Starting batch timer for converted events');
        startBatchTimer();
      }
    } else {
      // Individual mode: load events into individual queue as before
      queue.enqueue(...filteredItems);
    }
  }

  /**
   * Save events to local storage
   * @param events
   */
  function saveEventsToLocalStorage(events: QueueEvent[]) {
    if (!isLocalStorageAvailable()) {
      return;
    }

    // Only save to localStorage when in individual mode
    // In batch mode, we don't need localStorage persistence for individual events
    if (pluginOptions.is_batch) {
      return;
    }

    if (events.length > STORAGE_LIMIT) {
      warn(
        "Hit storage limit. Can't save all events to local storage. Keeping last",
        STORAGE_LIMIT
      );

      events = events.slice(events.length - STORAGE_LIMIT);
    }

    localStorage.setItem(STORAGE_NAME, JSON.stringify(events));
  }

  /**
   * This is a workaround to perform a GET request without getting blocked by ad-blockers
   *
   * @param url to call
   */
  async function requestAsImage(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // If the request took longer than we expect, just assume that it failed.
      const cancellation = setTimeout(() => {
        cancelled = true;
        reject('Sending event took too long.');
      }, REQUEST_TIMEOUT_SECONDS);
      const image = new Image(1, 1);
      let cancelled = false;
      image.onload = () => {
        if (!cancelled) {
          clearTimeout(cancellation);
          resolve();
        }
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      image.onerror = (e: any) => reject(e);
      image.src = url;
    });
  }

  /**
   * Send JSON event via POST request
   * @param jsonEvent JSON event to send
   * @param partyId party ID for query parameter
   * @return sent, dropped, or retry based on Govolte response
   */
  async function sendJsonEvent(
    jsonEvent: JsonEvent,
    originalEvent: Event,
    partyId: string
  ): Promise<SendEventResult> {
    // Check if fetch is available (browser environment)
    if (typeof fetch === 'undefined') {
      throw new Error('fetch is not available');
    }

    const url = `${pluginOptions.baseUrl}/${
      GOVOLTE_ENDPOINTS.submitEvent
    }?p=${encodeURIComponent(partyId)}`;
    const payload = jsonEventToShortenedFormat(jsonEvent, originalEvent);

    debug(TAG, 'Sending individual JSON POST request', {
      url,
      partyId,
      jsonEvent,
      originalEvent,
      payload,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const success = response.ok || response.status === 204; // Accept 204 No Content as success
      const nonRetryable = isNonRetryableJsonStatus(response.status);
      debug(
        TAG,
        `Individual JSON POST response: ${response.status} ${response.statusText}`,
        {
          success,
          nonRetryable,
          status: response.status,
          statusText: response.statusText,
        }
      );

      if (success) {
        return 'sent';
      }

      if (nonRetryable) {
        warn(
          TAG,
          `Dropping JSON event after non-retryable Govolte response: ${response.status} ${response.statusText}`
        );
        return 'dropped';
      }

      return 'retry';
    } catch (err) {
      warn('Error delivering JSON event', err);
      return 'retry';
    }
  }

  /**
   * Send batch of JSON events via POST request
   * @param events Array of JSON events to send
   * @param partyId party ID for query parameter
   * @return true if batch is successfully sent
   */
  async function sendJsonEventBatch(
    batchedEvents: BatchedQueueEvent[],
    partyId: string
  ): Promise<boolean> {
    // Check if fetch is available (browser environment)
    if (typeof fetch === 'undefined') {
      throw new Error('fetch is not available');
    }

    const url = `${pluginOptions.baseUrl}/${
      GOVOLTE_ENDPOINTS.submitEvent
    }?p=${encodeURIComponent(partyId)}`;
    const payload = batchedEvents.map(batchedEvent =>
      jsonEventToShortenedFormat(batchedEvent.jsonEvent, batchedEvent.event)
    );

    debug(TAG, 'Sending batch POST request', {
      url,
      partyId,
      batchSize: batchedEvents.length,
      payload,
    });

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const success = response.ok || response.status === 204; // Accept 204 No Content as success
      debug(
        TAG,
        `Batch POST response: ${response.status} ${response.statusText}`,
        {
          success,
          status: response.status,
          statusText: response.statusText,
        }
      );

      return success;
    } catch (err) {
      warn('Error delivering JSON event batch', err);
      return false;
    }
  }

  /**
   * Send individual event to govolte (only used when is_batch = false)
   * This function is only called from the queue system for individual events
   * @param govolteEvent Govolte event query params {@link GovolteEventQueryParams}
   * @return sent, dropped, or retry based on the request outcome
   */
  async function sendEvent(queueEvent: QueueEvent): Promise<SendEventResult> {
    const {originalEventType, originalEvent} = queueEvent;
    const govolteEvent = {...queueEvent} as Partial<QueueEvent>;
    // QueueEvent includes retry metadata and the original SDK event for JSON
    // POST/fallback handling. Strip those internal fields before using the
    // object as Govolte query parameters for the legacy GET request.
    delete govolteEvent.trialCount;
    delete govolteEvent.originalEventType;
    delete govolteEvent.originalEvent;

    debug(TAG, `Sending individual event: ${queueEvent.t}`, {
      govolteEvent,
      originalEventType,
      originalEvent,
    });

    // This function should only be called for individual mode (is_batch = false)
    // When is_batch = true, events go through addToBatch() -> sendBatch() instead
    if (originalEventType && originalEvent) {
      // Try JSON POST first (like iOS/Android)
      try {
        const jsonEvent = eventToJsonEvent(originalEventType, originalEvent);
        debug(TAG, 'Sending as JSON POST', {jsonEvent});
        const result = await sendJsonEvent(
          jsonEvent,
          originalEvent,
          originalEvent.party_id
        );
        debug(TAG, 'JSON POST result', {result});
        return result;
      } catch (err) {
        warn('Error sending JSON event, falling back to GET', err);
        // Fallback to original GET method
      }
    }

    // Fallback: use the original GET method
    const queryParams = qs.stringify(govolteEvent);
    const url = `${pluginOptions.baseUrl}/${GOVOLTE_ENDPOINTS.submitEvent}?${queryParams}`;
    debug(TAG, `Sending as GET request`, {url});
    try {
      await requestAsImage(url);
      debug(TAG, 'GET request successful');
      return 'sent';
    } catch (err) {
      warn('Error delivering event', err);
      return 'retry';
    }
  }

  /**
   * Start or reset the batch timer
   */
  function startBatchTimer() {
    if (batchTimer) {
      window.clearTimeout(batchTimer);
    }
    batchTimer = window.setTimeout(() => {
      sendBatch();
    }, pluginOptions.max_batch_age);
  }

  /**
   * Send the current batch of events
   */
  async function sendBatch() {
    if (isSendingBatch || batchQueue.isEmpty()) {
      return;
    }

    isSendingBatch = true;
    if (batchTimer) {
      window.clearTimeout(batchTimer);
      batchTimer = null;
    }

    const eventsToSend = batchQueue.getAllItems();
    const partyId = eventsToSend[0]?.event.party_id;

    if (!partyId) {
      isSendingBatch = false;
      return;
    }

    debug(TAG, `Sending batch of ${eventsToSend.length} events`, {
      eventsToSend: eventsToSend.map(e => ({
        eventType: e.eventType,
        event: e.event,
        jsonEvent: e.jsonEvent,
      })),
      partyId,
    });

    try {
      const success = await sendJsonEventBatch(eventsToSend, partyId);

      if (success) {
        debug(TAG, `Successfully sent batch of ${eventsToSend.length} events`);
        // Clear the batch queue
        batchQueue.clear();
      } else {
        debug(TAG, 'Failed to send batch, will retry individual events');
        // Move events back to individual processing
        eventsToSend.forEach(batchedEvent => {
          const govolteEvent = govolteEventFromEvent(
            batchedEvent.eventType,
            batchedEvent.event
          );
          const queueEvent: QueueEvent = {
            ...govolteEvent,
            trialCount: 0,
            originalEventType: batchedEvent.eventType,
            originalEvent: batchedEvent.event,
          };
          queue.enqueue(queueEvent);
        });
        batchQueue.clear();
        handleQueue();
      }
    } catch (err) {
      warn('Error sending batch', err);
      // Move events back to individual processing
      eventsToSend.forEach(batchedEvent => {
        const govolteEvent = govolteEventFromEvent(
          batchedEvent.eventType,
          batchedEvent.event
        );
        const queueEvent: QueueEvent = {
          ...govolteEvent,
          trialCount: 0,
          originalEventType: batchedEvent.eventType,
          originalEvent: batchedEvent.event,
        };
        queue.enqueue(queueEvent);
      });
      batchQueue.clear();
      handleQueue();
    }

    isSendingBatch = false;

    // If there are more events in the batch queue, start a new timer
    if (!batchQueue.isEmpty()) {
      startBatchTimer();
    }
  }

  /**
   * Add event to batch queue and trigger sending if batch is full
   */
  function addToBatch(eventType: EventType, event: Event) {
    const jsonEvent = eventToJsonEvent(eventType, event);
    const batchedEvent: BatchedQueueEvent = {
      eventType,
      event,
      jsonEvent,
      trialCount: 0,
      timestamp: Date.now(),
    };

    batchQueue.enqueue(batchedEvent);

    debug(
      TAG,
      `Event added to batch queue. Size: ${batchQueue.size()}/${
        pluginOptions.batch_size
      }`,
      {
        eventType,
        event,
        jsonEvent,
        batchSize: batchQueue.size(),
        maxBatchSize: pluginOptions.batch_size,
      }
    );

    // Start timer if this is the first event in the batch
    if (batchQueue.size() === 1) {
      debug(TAG, `Starting batch timer (${pluginOptions.max_batch_age}ms)`);
      startBatchTimer();
    }

    // Send batch if it's full
    if (batchQueue.size() >= pluginOptions.batch_size) {
      debug(TAG, 'Batch is full, sending immediately');
      sendBatch();
    }
  }

  /**
   * Manage event queue by trying to send one event at a time to govolte
   * @param isTriggeredFromNewEvent false if this is the same event trying to be re-sent, defaults to true.
   */
  function handleQueue(isTriggeredFromNewEvent = true) {
    // Don't try to send another event when the plugin is busy sending another event
    // Only send one event at a time
    if (isSendingEvent && isTriggeredFromNewEvent) {
      return;
    }

    // Check if we have any events in the queue
    const event = queue.peek();
    if (!event) {
      return;
    }

    // Destructure the event to a GovolteEventQueryParams object
    const {
      trialCount: savedTrialCount,
      originalEventType,
      originalEvent,
      ...govolteEvent
    } = event;

    // Increment the event trial count and update it in the queue
    const trialCount = isNaN(savedTrialCount) ? 1 : savedTrialCount + 1;
    const queueEvent: QueueEvent = {
      ...govolteEvent,
      trialCount,
      originalEventType,
      originalEvent,
    };
    queue.swapTopElement(queueEvent);
    saveEventsToLocalStorage(queue.getAllItems());

    debug(
      TAG,
      `Sending event [attempt ${trialCount}]: ${govolteEvent.t}`,
      govolteEvent
    );

    // If there's an event, then actually send it
    isSendingEvent = true;
    sendEvent(queueEvent).then(result => {
      // If the event is successfully sent or has already been retried
      // more than `maxRetryCount` times, then remove it from queue
      if (result !== 'retry' || trialCount > maxRetryCount) {
        isSendingEvent = false;

        if (result === 'sent') {
          debug(
            TAG,
            `Successfully sent event [attempt ${trialCount}]: ${govolteEvent.t}`
          );
        } else if (result === 'dropped') {
          debug(
            TAG,
            `Dropping event after non-retryable response [attempt ${trialCount}]: ${govolteEvent.t}`
          );
        } else {
          debug(
            TAG,
            `Removing event because already tried sending ${trialCount} times. MaxRetryCount is ${maxRetryCount}. Event: ${govolteEvent.t}`
          );
        }
        queue.dequeue();
        saveEventsToLocalStorage(queue.getAllItems());
        handleQueue(false);
      } else {
        const delayDuration = delayBetweenRetriesInMs * trialCount;
        debug(
          TAG,
          `Failed sending event: ${govolteEvent.t}. Now waiting for ${delayDuration}ms before retry [attempt ${trialCount}/${maxRetryCount}].`
        );
        sleep(delayDuration).then(() => handleQueue(false));
      }
    });
  }

  // Load unsent events
  loadPreviousEventsFromLocalStorage();
  // Then try to send it again
  handleQueue(false);

  return {
    submitEvent(eventType: EventType, event: Event) {
      // Log the event being submitted
      debug(TAG, `Submitting event: ${eventType}`, {
        eventType,
        event,
        batchMode: pluginOptions.is_batch,
      });

      // Check if the event should be sent or throttled
      if (throttleEvent(eventType, event, eventThrottlers)) {
        debug(TAG, `Event throttled: ${eventType}`);
        return;
      }

      if (pluginOptions.is_batch) {
        // Use batching mode
        debug(TAG, `Adding event to batch: ${eventType}`);
        addToBatch(eventType, event);
      } else {
        // Use individual JSON event sending (like iOS/Android) but with queue for reliability
        const govolteEvent = govolteEventFromEvent(eventType, event);
        const trialCountEvent: QueueEvent = {
          ...govolteEvent,
          trialCount: 0,
          originalEventType: eventType,
          originalEvent: event,
        };
        debug(
          TAG,
          `Adding event to individual queue: ${eventType}`,
          govolteEvent
        );
        queue.enqueue(trialCountEvent);
        saveEventsToLocalStorage(queue.getAllItems());
        handleQueue();
      }
    },

    queuedEvents(): GovolteEventQueryParams[] {
      return queue.getAllItems();
    },

    isSendingEvent(): boolean {
      return isSendingEvent || isSendingBatch;
    },

    delayBetweenRetriesInMs,

    maxRetryCount,

    delayBetweenStreamWaypointsInMs,

    // Batching properties
    is_batch: pluginOptions.is_batch,
    batch_size: pluginOptions.batch_size,
    max_batch_age: pluginOptions.max_batch_age,

    // all our session data (like party_id, session_id) is already in the NPOTag class
    getSessionInfo: () => ({}),
    initializeFromSessionInfo() {},
  };
}
